import { useMemo, useState } from "react";

import { useE2EAgentUsageRuns, type E2EAgentUsageRun } from "../../api/admin";
import {
  aggregateUsage,
  formatDurationSeconds,
  formatPercent,
  formatSubagentsPerRun,
  formatTokenCount,
  formatUsd,
  summarizeUsage,
  type UsageBucket,
  type UsageGranularity,
  type UsageTotals,
} from "../../lib/usageAggregation";
import {
  Selector,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type SelectorOption,
} from "../ui";
import { PageHeader, SegmentedControl } from "../WorkspacePrimitives";
import {
  UsageBarChart,
  UsageLineChart,
  type BarSeries,
  type LineSeries,
} from "./UsageCharts";
import "./usage.css";

const RANGE_OPTIONS: readonly SelectorOption<string>[] = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 180 days" },
  { value: "365", label: "Last 365 days" },
];

const GRANULARITY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
] as const;

const COMPARISON_OPTIONS = [
  { value: "none", label: "None" },
  { value: "user", label: "Users" },
  { value: "model", label: "Models" },
] as const;

const ALL = "";
type ComparisonDimension = (typeof COMPARISON_OPTIONS)[number]["value"];

interface UsageCohort {
  key: string;
  label: string;
  color: string;
  softColor: string;
  runs: readonly E2EAgentUsageRun[];
  totals: UsageTotals;
  buckets: readonly UsageBucket[];
}

interface StatValue {
  key: string;
  label?: string;
  color: string;
  value: string;
  hint?: string;
}

function starterKey(run: E2EAgentUsageRun): string {
  return run.started_by?.subject ?? "";
}

function starterLabel(run: E2EAgentUsageRun): string {
  const starter = run.started_by;
  if (!starter) return "Unattributed";
  return starter.display_name || starter.email || starter.subject;
}

function resolveComparisonPair(
  options: readonly SelectorOption<string>[],
  requestedLeft: string,
  requestedRight: string,
): readonly [string, string] {
  const values = options.map((option) => option.value);
  const left = values.includes(requestedLeft)
    ? requestedLeft
    : (values[0] ?? "");
  const right =
    requestedRight !== left && values.includes(requestedRight)
      ? requestedRight
      : (values.find((value) => value !== left) ?? left);
  return [left, right];
}

function selectedLabel(
  options: readonly SelectorOption<string>[],
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function matchingBucket(
  cohort: UsageCohort,
  key: string,
): UsageBucket | undefined {
  return cohort.buckets.find((bucket) => bucket.key === key);
}

export default function E2EAgentUsagePage() {
  const [rangeDays, setRangeDays] = useState("30");
  const [granularity, setGranularity] = useState<UsageGranularity>("daily");
  const [comparison, setComparison] = useState<ComparisonDimension>("none");
  const [user, setUser] = useState(ALL);
  const [model, setModel] = useState(ALL);
  const [requestedUserLeft, setRequestedUserLeft] = useState(ALL);
  const [requestedUserRight, setRequestedUserRight] = useState(ALL);
  const [requestedModelLeft, setRequestedModelLeft] = useState(ALL);
  const [requestedModelRight, setRequestedModelRight] = useState(ALL);

  const since = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (Number(rangeDays) - 1));
    return start;
  }, [rangeDays]);
  const usage = useE2EAgentUsageRuns(since.toISOString());
  const allRuns = useMemo(() => usage.data?.runs ?? [], [usage.data]);

  const userChoices = useMemo<readonly SelectorOption<string>[]>(() => {
    const seen = new Map<string, string>();
    for (const run of allRuns) {
      const key = starterKey(run);
      if (key && !seen.has(key)) seen.set(key, starterLabel(run));
    }
    return [...seen.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [allRuns]);
  const userOptions = useMemo<readonly SelectorOption<string>[]>(
    () => [{ value: ALL, label: "All users" }, ...userChoices],
    [userChoices],
  );

  const modelChoices = useMemo<readonly SelectorOption<string>[]>(
    () =>
      [...new Set(allRuns.map((run) => run.model))]
        .sort()
        .map((value) => ({ value, label: value })),
    [allRuns],
  );
  const modelOptions = useMemo<readonly SelectorOption<string>[]>(
    () => [{ value: ALL, label: "All models" }, ...modelChoices],
    [modelChoices],
  );

  const [userLeft, userRight] = resolveComparisonPair(
    userChoices,
    requestedUserLeft,
    requestedUserRight,
  );
  const [modelLeft, modelRight] = resolveComparisonPair(
    modelChoices,
    requestedModelLeft,
    requestedModelRight,
  );

  const cohorts = useMemo<UsageCohort[]>(() => {
    const until = new Date();
    const makeCohort = (
      key: string,
      label: string,
      color: string,
      softColor: string,
      runs: readonly E2EAgentUsageRun[],
    ): UsageCohort => ({
      key,
      label,
      color,
      softColor,
      runs,
      totals: summarizeUsage(runs),
      buckets: aggregateUsage(runs, granularity, since, until),
    });

    if (comparison === "user") {
      return [userLeft, userRight]
        .filter(
          (value, index, values) =>
            value !== ALL && values.indexOf(value) === index,
        )
        .map((value, index) =>
          makeCohort(
            `user:${value}`,
            selectedLabel(userChoices, value),
            index === 0 ? "var(--usage-series-1)" : "var(--usage-series-2)",
            index === 0
              ? "var(--usage-series-1-soft)"
              : "var(--usage-series-2-soft)",
            allRuns.filter(
              (run) =>
                starterKey(run) === value &&
                (model === ALL || run.model === model),
            ),
          ),
        );
    }
    if (comparison === "model") {
      return [modelLeft, modelRight]
        .filter(
          (value, index, values) =>
            value !== ALL && values.indexOf(value) === index,
        )
        .map((value, index) =>
          makeCohort(
            `model:${value}`,
            selectedLabel(modelChoices, value),
            index === 0 ? "var(--usage-series-1)" : "var(--usage-series-2)",
            index === 0
              ? "var(--usage-series-1-soft)"
              : "var(--usage-series-2-soft)",
            allRuns.filter(
              (run) =>
                run.model === value &&
                (user === ALL || starterKey(run) === user),
            ),
          ),
        );
    }

    const runs = allRuns.filter(
      (run) =>
        (user === ALL || starterKey(run) === user) &&
        (model === ALL || run.model === model),
    );
    return [
      makeCohort(
        "selected",
        "Selected runs",
        "var(--usage-series-1)",
        "var(--usage-series-1-soft)",
        runs,
      ),
    ];
  }, [
    allRuns,
    comparison,
    granularity,
    model,
    modelChoices,
    modelLeft,
    modelRight,
    since,
    user,
    userChoices,
    userLeft,
    userRight,
  ]);

  const isComparison = cohorts.length > 1;
  const buckets = cohorts[0]?.buckets ?? [];
  const runCount = cohorts.reduce(
    (total, cohort) => total + cohort.runs.length,
    0,
  );
  const statValues = (
    value: (totals: UsageTotals) => string,
    hint?: (totals: UsageTotals) => string | undefined,
  ): StatValue[] =>
    cohorts.map((cohort) => ({
      key: cohort.key,
      label: isComparison ? cohort.label : undefined,
      color: cohort.color,
      value: value(cohort.totals),
      hint: hint?.(cohort.totals),
    }));
  const seriesName = (cohort: UsageCohort, fallback: string) =>
    isComparison ? cohort.label : fallback;

  const successSeries: LineSeries[] = cohorts.map((cohort) => ({
    key: cohort.key,
    name: seriesName(cohort, "Success rate"),
    color: cohort.color,
    values: cohort.buckets.map((bucket) =>
      bucket.successRate === null ? null : bucket.successRate * 100,
    ),
    format: (value) => `${value.toFixed(0)}%`,
  }));
  const durationSeries: LineSeries[] = cohorts.flatMap((cohort) => [
    {
      key: `${cohort.key}:median`,
      name: isComparison ? `${cohort.label} median` : "Median",
      color: cohort.color,
      values: cohort.buckets.map((bucket) => bucket.medianDurationSeconds),
      format: formatDurationSeconds,
    },
    {
      key: `${cohort.key}:p90`,
      name: isComparison ? `${cohort.label} p90` : "p90",
      color: cohort.softColor,
      values: cohort.buckets.map((bucket) => bucket.p90DurationSeconds),
      format: formatDurationSeconds,
    },
  ]);
  const costSeries: BarSeries[] = cohorts.map((cohort) => ({
    key: cohort.key,
    name: seriesName(cohort, "Estimated cost"),
    color: cohort.color,
    values: cohort.buckets.map((bucket) =>
      bucket.costedRuns > 0 ? bucket.estimatedCostUsd : null,
    ),
    format: formatUsd,
  }));
  const tokenSeries: BarSeries[] = cohorts.map((cohort) => ({
    key: cohort.key,
    name: seriesName(cohort, "Total tokens"),
    color: cohort.color,
    values: cohort.buckets.map((bucket) =>
      bucket.tokenCountedRuns > 0 ? bucket.totalTokens : null,
    ),
    format: formatTokenCount,
  }));
  const runSeries: BarSeries[] = cohorts.map((cohort) => ({
    key: cohort.key,
    name: seriesName(cohort, "Runs"),
    color: cohort.color,
    values: cohort.buckets.map((bucket) => bucket.runs),
    format: (value) => String(Math.round(value)),
  }));
  const coverageRows = (
    bucket: UsageBucket,
    key: string,
    label: string,
    value: (current: UsageBucket) => string,
  ) =>
    cohorts.flatMap((cohort) => {
      const current = matchingBucket(cohort, bucket.key);
      if (!current) return [];
      return [
        {
          key: `${cohort.key}:${key}`,
          label: isComparison ? `${cohort.label}: ${label}` : label,
          value: value(current),
        },
      ];
    });

  return (
    <section
      className="build-ledger-page e2e-admin-page usage-page"
      aria-labelledby="e2e-agent-usage-title"
    >
      <PageHeader className="e2e-admin-page-header">
        <div className="build-view-intro">
          <div>
            <span className="section-kicker">Isolated Ryvn execution</span>
            <h2 id="e2e-agent-usage-title">Usage</h2>
            <p>
              Compare success, duration, agent, token, and cost metrics by user
              or model.
            </p>
          </div>
        </div>
        <div className="build-ledger-toolbar" aria-label="Usage filters">
          <Selector
            options={RANGE_OPTIONS}
            value={rangeDays}
            onValueChange={setRangeDays}
            label="Date range"
            className="compact-field"
          />
          <SegmentedControl
            label="Granularity"
            value={granularity}
            options={[...GRANULARITY_OPTIONS]}
            onChange={setGranularity}
          />
          <SegmentedControl
            label="Compare"
            value={comparison}
            options={[...COMPARISON_OPTIONS]}
            onChange={setComparison}
          />
          {comparison === "user" ? (
            <>
              <Selector
                options={userChoices.filter(
                  (option) => option.value !== userRight,
                )}
                value={userLeft}
                onValueChange={setRequestedUserLeft}
                label="User A"
                searchable
                className="compact-field"
              />
              <Selector
                options={userChoices.filter(
                  (option) => option.value !== userLeft,
                )}
                value={userRight}
                onValueChange={setRequestedUserRight}
                label="User B"
                searchable
                className="compact-field"
              />
            </>
          ) : (
            <Selector
              options={userOptions}
              value={user}
              onValueChange={setUser}
              label="User"
              searchable
              className="compact-field"
            />
          )}
          {comparison === "model" ? (
            <>
              <Selector
                options={modelChoices.filter(
                  (option) => option.value !== modelRight,
                )}
                value={modelLeft}
                onValueChange={setRequestedModelLeft}
                label="Model A"
                className="compact-field"
              />
              <Selector
                options={modelChoices.filter(
                  (option) => option.value !== modelLeft,
                )}
                value={modelRight}
                onValueChange={setRequestedModelRight}
                label="Model B"
                className="compact-field"
              />
            </>
          ) : (
            <Selector
              options={modelOptions}
              value={model}
              onValueChange={setModel}
              label="Model"
              className="compact-field"
            />
          )}
          <span className="build-row-count">
            {isComparison
              ? cohorts
                  .map((cohort) => `${cohort.label}: ${cohort.runs.length}`)
                  .join(" · ")
              : `${runCount} runs`}
            {usage.data?.truncated ? " (window truncated)" : ""}
          </span>
        </div>
      </PageHeader>

      {usage.isLoading && !usage.data && (
        <div className="state">Loading usage…</div>
      )}
      {usage.error && !usage.data && (
        <div className="state err">
          Failed to load usage: {usage.error.message}
        </div>
      )}

      {usage.data && (
        <div
          className={
            usage.isFetching ? "usage-body usage-body--stale" : "usage-body"
          }
        >
          <div className="usage-kpis">
            <StatTile
              label="Runs"
              values={statValues((totals) => String(totals.runs))}
            />
            <StatTile
              label="Success rate"
              values={statValues(
                (totals) => formatPercent(totals.successRate),
                (totals) => `${totals.succeeded} of ${totals.decided} decided`,
              )}
            />
            <StatTile
              label="Median duration"
              values={statValues(
                (totals) => formatDurationSeconds(totals.medianDurationSeconds),
                () => "terminal runs",
              )}
            />
            <StatTile
              label="Subagents / run"
              values={statValues(
                (totals) => formatSubagentsPerRun(totals.avgSubagentsPerRun),
                (totals) =>
                  totals.tracedRuns > 0
                    ? `average over ${totals.tracedRuns} runs with a trace summary`
                    : "no trace summaries in range",
              )}
            />
            <StatTile
              label="Total tokens"
              values={statValues(
                (totals) =>
                  formatTokenCount(
                    totals.tokenCountedRuns > 0 ? totals.totalTokens : null,
                  ),
                (totals) =>
                  totals.tokenCountedRuns > 0
                    ? `${totals.tokenCountedRuns} runs with a recorded total`
                    : "no token totals in range",
              )}
            />
            <StatTile
              label="Estimated cost"
              values={statValues(
                (totals) =>
                  formatUsd(
                    totals.costedRuns > 0 ? totals.estimatedCostUsd : null,
                  ),
                (totals) =>
                  totals.costedRuns > 0
                    ? `${totals.costedRuns} runs with a recorded estimate`
                    : "no trace summaries in range",
              )}
            />
            <StatTile
              label="Cost per run"
              values={statValues(
                (totals) => formatUsd(totals.avgCostPerRunUsd),
                (totals) =>
                  totals.costedRuns > 0
                    ? `average over ${totals.costedRuns} costed runs`
                    : "no trace summaries in range",
              )}
            />
          </div>

          {runCount === 0 ? (
            <div className="build-empty-state">
              <strong>No runs match.</strong>
              <span>Widen the range or change the comparison and filters.</span>
            </div>
          ) : (
            <>
              <div className="usage-charts">
                <UsageLineChart
                  title="Success rate"
                  buckets={buckets}
                  series={successSeries}
                  yMax={100}
                  yTickFormat={(value) => `${value.toFixed(0)}%`}
                  tooltipExtra={(bucket) =>
                    cohorts.flatMap((cohort) => {
                      const current = matchingBucket(cohort, bucket.key);
                      if (!current) return [];
                      const prefix = isComparison ? `${cohort.label}: ` : "";
                      return [
                        {
                          key: `${cohort.key}:counts`,
                          label: `${prefix}succeeded / failed`,
                          value: `${current.succeeded} / ${current.failed + current.expired}`,
                        },
                        ...(current.canceled > 0
                          ? [
                              {
                                key: `${cohort.key}:canceled`,
                                label: `${prefix}canceled (not counted)`,
                                value: String(current.canceled),
                              },
                            ]
                          : []),
                      ];
                    })
                  }
                />
                <UsageLineChart
                  title="Execution duration"
                  buckets={buckets}
                  series={durationSeries}
                  yTickFormat={formatDurationSeconds}
                  tooltipExtra={(bucket) =>
                    cohorts.flatMap((cohort) => {
                      const current = matchingBucket(cohort, bucket.key);
                      if (!current) return [];
                      return [
                        {
                          key: `${cohort.key}:timed`,
                          label: isComparison
                            ? `${cohort.label}: terminal runs timed`
                            : "terminal runs timed",
                          value: String(
                            current.succeeded +
                              current.failed +
                              current.expired,
                          ),
                        },
                      ];
                    })
                  }
                />
                <UsageBarChart
                  title="Estimated cost"
                  buckets={buckets}
                  series={costSeries}
                  yTickFormat={formatUsd}
                  tooltipExtra={(bucket) =>
                    coverageRows(
                      bucket,
                      "costed",
                      "runs with an estimate",
                      (current) => `${current.costedRuns} of ${current.runs}`,
                    )
                  }
                />
                <UsageLineChart
                  title="Cost per run"
                  buckets={buckets}
                  series={cohorts.map((cohort) => ({
                    key: cohort.key,
                    name: seriesName(cohort, "Average cost per run"),
                    color: cohort.color,
                    values: cohort.buckets.map(
                      (bucket) => bucket.avgCostPerRunUsd,
                    ),
                    format: formatUsd,
                  }))}
                  yTickFormat={formatUsd}
                  tooltipExtra={(bucket) =>
                    coverageRows(
                      bucket,
                      "costed-per-run",
                      "runs with an estimate",
                      (current) => `${current.costedRuns} of ${current.runs}`,
                    )
                  }
                />
                <UsageLineChart
                  title="Subagents per run"
                  buckets={buckets}
                  series={cohorts.map((cohort) => ({
                    key: cohort.key,
                    name: seriesName(cohort, "Average subagents per run"),
                    color: cohort.color,
                    values: cohort.buckets.map(
                      (bucket) => bucket.avgSubagentsPerRun,
                    ),
                    format: formatSubagentsPerRun,
                  }))}
                  yTickFormat={formatSubagentsPerRun}
                  tooltipExtra={(bucket) =>
                    coverageRows(
                      bucket,
                      "traced",
                      "runs with a trace summary",
                      (current) => `${current.tracedRuns} of ${current.runs}`,
                    )
                  }
                />
                <UsageBarChart
                  title="Total tokens"
                  buckets={buckets}
                  series={tokenSeries}
                  yTickFormat={formatTokenCount}
                  tooltipExtra={(bucket) =>
                    coverageRows(
                      bucket,
                      "token-counted",
                      "runs with recorded tokens",
                      (current) =>
                        `${current.tokenCountedRuns} of ${current.runs}`,
                    )
                  }
                />
                <UsageBarChart
                  title="Runs launched"
                  buckets={buckets}
                  series={runSeries}
                  yTickFormat={(value) => String(Math.round(value))}
                  tooltipExtra={(bucket) =>
                    cohorts.flatMap((cohort) => {
                      const current = matchingBucket(cohort, bucket.key);
                      if (!current || current.inFlight === 0) return [];
                      return [
                        {
                          key: `${cohort.key}:in-flight`,
                          label: isComparison
                            ? `${cohort.label}: still in flight`
                            : "still in flight",
                          value: String(current.inFlight),
                        },
                      ];
                    })
                  }
                />
              </div>

              <section
                className="usage-table"
                aria-label="Usage by period, table view"
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Period</TableHeader>
                      {isComparison && <TableHeader>Cohort</TableHeader>}
                      <TableHeader>Runs</TableHeader>
                      <TableHeader>Succeeded</TableHeader>
                      <TableHeader>Failed</TableHeader>
                      <TableHeader>Success rate</TableHeader>
                      <TableHeader>Median duration</TableHeader>
                      <TableHeader>p90 duration</TableHeader>
                      <TableHeader>Subagents / run</TableHeader>
                      <TableHeader>Total tokens</TableHeader>
                      <TableHeader>Estimated cost</TableHeader>
                      <TableHeader>Cost / run</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cohorts.flatMap((cohort) =>
                      cohort.buckets
                        .filter((bucket) => bucket.runs > 0)
                        .map((bucket) => (
                          <TableRow key={`${cohort.key}:${bucket.key}`}>
                            <TableCell>{bucket.label}</TableCell>
                            {isComparison && (
                              <TableCell>
                                <span className="usage-cohort-label">
                                  <span
                                    className="usage-cohort-swatch"
                                    style={{ background: cohort.color }}
                                    aria-hidden="true"
                                  />
                                  {cohort.label}
                                </span>
                              </TableCell>
                            )}
                            <TableCell className="usage-num">
                              {bucket.runs}
                            </TableCell>
                            <TableCell className="usage-num">
                              {bucket.succeeded}
                            </TableCell>
                            <TableCell className="usage-num">
                              {bucket.failed + bucket.expired}
                            </TableCell>
                            <TableCell className="usage-num">
                              {formatPercent(bucket.successRate)}
                            </TableCell>
                            <TableCell className="usage-num">
                              {formatDurationSeconds(
                                bucket.medianDurationSeconds,
                              )}
                            </TableCell>
                            <TableCell className="usage-num">
                              {formatDurationSeconds(bucket.p90DurationSeconds)}
                            </TableCell>
                            <TableCell className="usage-num">
                              {formatSubagentsPerRun(bucket.avgSubagentsPerRun)}
                            </TableCell>
                            <TableCell className="usage-num">
                              {formatTokenCount(
                                bucket.tokenCountedRuns > 0
                                  ? bucket.totalTokens
                                  : null,
                              )}
                            </TableCell>
                            <TableCell className="usage-num">
                              {bucket.costedRuns > 0
                                ? formatUsd(bucket.estimatedCostUsd)
                                : "—"}
                            </TableCell>
                            <TableCell className="usage-num">
                              {formatUsd(bucket.avgCostPerRunUsd)}
                            </TableCell>
                          </TableRow>
                        )),
                    )}
                  </TableBody>
                </Table>
              </section>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function StatTile({
  label,
  values,
}: {
  label: string;
  values: readonly StatValue[];
}) {
  return (
    <div className="usage-stat">
      <span className="usage-stat__label">{label}</span>
      <div className="usage-stat__cohorts">
        {values.map((item) => (
          <div className="usage-stat__cohort" key={item.key}>
            {item.label && (
              <span className="usage-cohort-label usage-stat__cohort-label">
                <span
                  className="usage-cohort-swatch"
                  style={{ background: item.color }}
                  aria-hidden="true"
                />
                {item.label}
              </span>
            )}
            <span className="usage-stat__value">{item.value}</span>
            {item.hint && <span className="usage-stat__hint">{item.hint}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
